// SubjectsStep.js - Second step: Select subjects of interest
import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';

const SUBJECTS = [
  { id: 'biology', label: 'Biology', icon: 'dna' },
  { id: 'chemistry', label: 'Chemistry', icon: 'flask' },
  { id: 'physics', label: 'Physics', icon: 'atom' },
  { id: 'math', label: 'Math', icon: 'calculator-variant' },
  { id: 'geography', label: 'Geography', icon: 'earth' },
  { id: 'anatomy', label: 'Anatomy', icon: 'human' },
];

const SubjectsStep = ({ selectedSubjects = [], onSubjectsChange, onNext, onBack }) => {
  const { isDark } = useTheme();
  const [selected, setSelected] = useState(selectedSubjects);

  useEffect(() => {
    setSelected(selectedSubjects);
  }, [selectedSubjects]);

  const colors = {
    bg: isDark ? '#1e293b' : '#f8fafc',
    card: isDark ? '#334155' : '#ffffff',
    text: isDark ? '#f8fafc' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    accent: '#2563eb',
    accentLight: isDark ? '#2563eb30' : '#2563eb15',
    border: isDark ? '#475569' : '#e2e8f0',
    selectedBorder: '#2563eb',
    selectedBg: isDark ? '#2563eb25' : '#2563eb10',
  };

  const toggleSubject = (subjectId) => {
    const newSelected = selected.includes(subjectId)
      ? selected.filter((s) => s !== subjectId)
      : [...selected, subjectId];
    setSelected(newSelected);
    onSubjectsChange?.(newSelected);
  };

  const handleContinue = () => {
    if (selected.length > 0) {
      onNext();
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.bg }]}>
      <View style={[styles.content, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Title */}
        <Text style={[styles.title, { color: colors.text }]}>
          What subjects interest you?
        </Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          Select all that apply
        </Text>

        {/* Subject Grid */}
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.subjectsGrid}
          showsVerticalScrollIndicator={false}
        >
          {SUBJECTS.map((subject) => {
            const isSelected = selected.includes(subject.id);
            return (
              <TouchableOpacity
                key={subject.id}
                style={[
                  styles.subjectCard,
                  {
                    backgroundColor: isSelected ? colors.selectedBg : colors.bg,
                    borderColor: isSelected ? colors.selectedBorder : colors.border,
                  },
                ]}
                onPress={() => toggleSubject(subject.id)}
                activeOpacity={0.7}
              >
                <View
                  style={[
                    styles.iconWrapper,
                    {
                      backgroundColor: isSelected ? colors.accentLight : colors.bg,
                    },
                  ]}
                >
                  <MaterialCommunityIcons
                    name={subject.icon}
                    size={28}
                    color={isSelected ? colors.accent : colors.textSecondary}
                  />
                </View>
                <Text
                  style={[
                    styles.subjectLabel,
                    { color: isSelected ? colors.accent : colors.text },
                  ]}
                >
                  {subject.label}
                </Text>
                {isSelected && (
                  <View style={[styles.checkmark, { backgroundColor: colors.accent }]}>
                    <MaterialCommunityIcons name="check" size={14} color="#fff" />
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        {/* Selected count */}
        {selected.length > 0 && (
          <Text style={[styles.selectedCount, { color: colors.textSecondary }]}>
            {selected.length} subject{selected.length !== 1 ? 's' : ''} selected
          </Text>
        )}

        {/* Navigation Buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.secondaryButton, { borderColor: colors.border }]}
            onPress={onBack}
            activeOpacity={0.7}
          >
            <MaterialCommunityIcons name="arrow-left" size={18} color={colors.text} />
            <Text style={[styles.secondaryButtonText, { color: colors.text }]}>Back</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.primaryButton,
              {
                backgroundColor: selected.length > 0 ? colors.accent : colors.border,
                opacity: selected.length > 0 ? 1 : 0.6,
              },
            ]}
            onPress={handleContinue}
            activeOpacity={0.8}
            disabled={selected.length === 0}
          >
            <Text style={styles.primaryButtonText}>Continue</Text>
            <MaterialCommunityIcons name="arrow-right" size={18} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 16,
    padding: 32,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
    maxHeight: '90%',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 24,
  },
  scrollView: {
    maxHeight: 320,
  },
  subjectsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12,
    paddingBottom: 8,
  },
  subjectCard: {
    width: 140,
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: 'center',
    position: 'relative',
  },
  iconWrapper: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
  },
  subjectLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  checkmark: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedCount: {
    fontSize: 13,
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 8,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 24,
    gap: 12,
  },
  secondaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    gap: 6,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  primaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    gap: 6,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
});

export default SubjectsStep;
